// Mirrors com.mytokenguard.network.Api.kt 1:1 -- same endpoints, same request
// bodies, same response shapes. Website-specific version: always plain
// fetch(). The desktop app's version tries @tauri-apps/plugin-http first
// (so requests bypass the desktop webview's CORS restrictions) -- that
// doesn't apply here, this is a normal browser page hitting a real API
// origin, and doesn't have that package installed anyway (a dynamic
// `import("@tauri-apps/plugin-http")` would still fail to resolve at Vite
// build time even though it's dynamic, since Rollup statically analyzes
// string-literal import specifiers for code-splitting -- not just a
// runtime concern).
import { getBaseUrl } from "./config";
import { bearer, clearSession } from "./session";

class ApiError extends Error {
  constructor(message, status, payload = null) {
    super(message);
    this.status = status;
    // Parsed JSON body of the error response, when there was one (e.g.
    // { error, otp_required } or { error, captcha_required } from
    // POST /api/auth/login). null for network errors or non-JSON bodies.
    this.payload = payload;
  }
}

async function request(path, { method = "GET", body, auth = false, signal } = {}) {
  const url = new URL(path.replace(/^\//, ""), getBaseUrl()).toString();

  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const token = await bearer();
    if (!token) throw new ApiError("Not signed in", 401);
    headers["Authorization"] = token;
  }

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    throw new ApiError(`Network error: ${e.message || "Unknown error"}`, 0);
  }

  // A 401 on an authenticated call means the session is dead -- match the
  // desktop/Android apps' behaviour of dropping straight back to Login.
  if (res.status === 401 && auth) {
    await clearSession();
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `Server returned ${res.status}`;
    throw new ApiError(msg, res.status, data);
  }
  return data;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const Api = {
  // ---- Auth -------------------------------------------------------------
  register: (name, email, password, phone) =>
    request("/api/auth/register", { method: "POST", body: { name, email, password, phone } }),
  login: (email, password, captchaToken) =>
    request("/api/auth/login", { method: "POST", body: { email, password, captcha_token: captchaToken } }),
  requestOtp: (email) =>
    request("/api/auth/otp/request", { method: "POST", body: { email } }),
  verifyOtp: (email, code) =>
    request("/api/auth/otp/verify", { method: "POST", body: { email, code } }),
  forgotPassword: (email) =>
    request("/api/auth/password/forgot", { method: "POST", body: { email } }),
  resetPassword: (token, password) =>
    request("/api/auth/password/reset", { method: "POST", body: { token, password } }),
  deleteAccount: () => request("/api/account", { method: "DELETE", auth: true }),

  // ---- Profile ------------------------------------------------------------
  getProfile: () => request("/api/profile", { auth: true }),
  updateProfile: (name, phone, fcm_token) =>
    request("/api/profile", { method: "PATCH", auth: true, body: { name, phone, fcm_token } }),

  // ---- Plans ----------------------------------------------------------------
  listPlans: () => request("/api/plans", { auth: true }),
  selectPlan: (plan_id) => request("/api/plan/select", { method: "POST", auth: true, body: { plan_id } }),
  skipPlan: () => request("/api/plan/skip", { method: "POST", auth: true }),

  // ---- Monitor / thresholds --------------------------------------------------
  getMonitor: () => request("/api/monitor", { auth: true }),
  setThreshold: (payload) => request("/api/monitor/threshold", { method: "POST", auth: true, body: payload }),
  reactivateCutoff: (id) => request(`/api/monitor/credential/${id}/reactivate`, { method: "POST", auth: true }),
  resetCredentialUsage: (id) => request(`/api/monitor/credential/${id}/reset`, { method: "POST", auth: true }),

  // ---- Widget config ----------------------------------------------------------
  getWidgetConfig: () => request("/api/widget", { auth: true }),
  setWidgetConfig: (cfg) => request("/api/widget", { method: "POST", auth: true, body: cfg }),

  // ---- Providers / credentials --------------------------------------------------
  listCredentials: () => request("/api/llm/credentials", { auth: true }),
  addStoredKey: (llm, key) => request("/api/llm/key", { method: "POST", auth: true, body: { llm, key } }),
  deleteCredential: (id) => request(`/api/llm/credential/${id}`, { method: "DELETE", auth: true }),
  generatePassThrough: (llm) => request(`/api/llm/${llm}/passthrough`, { method: "POST", auth: true }),
  listAvailableLlms: () => request("/api/llm/available", { auth: true }),

  // ---- Health -------------------------------------------------------------
  // No baseUrlOverride/"Test connection" support here -- that only made
  // sense alongside the desktop app's ServerSettingsDialog, which the
  // website intentionally doesn't have (see lib/config.js).
  healthz: () => request("/healthz"),
};

export { ApiError };
